import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, Play } from 'lucide-react'
import type { InstagramReel } from '@/types'
import {
  instagramEmbedUrl,
  instagramMediaKind,
  normalizeInstagramMediaUrl,
} from '@/lib/instagramMedia'
import { InstagramBrandIcon } from '@/components/icons/SocialBrandIcons'
import { cn } from '@/lib/utils'

const kindLabel: Record<NonNullable<ReturnType<typeof instagramMediaKind>>, string> = {
  reel: 'Reel',
  post: 'Post',
  igtv: 'IGTV',
}

function ReelCardBackdrop({ thumbnail }: { thumbnail?: string }) {
  return (
    <>
      {thumbnail && (
        <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden>
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full scale-105 object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
      )}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-br from-luxury-900 via-[#1a1410] to-luxury-950',
          thumbnail && 'opacity-70'
        )}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[2] opacity-40"
        aria-hidden
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, rgba(249,224,118,0.22), transparent 45%), radial-gradient(circle at 80% 80%, rgba(217,119,6,0.15), transparent 40%)',
        }}
      />
    </>
  )
}

interface FeaturedVideoCardProps {
  reel: InstagramReel
}

export function FeaturedVideoCard({ reel }: FeaturedVideoCardProps) {
  const [playing, setPlaying] = useState(false)
  const [iframeReady, setIframeReady] = useState(false)
  const embed = instagramEmbedUrl(reel.url)
  const reelUrl = normalizeInstagramMediaUrl(reel.url)
  const kind = instagramMediaKind(reel.url)

  if (!embed || !reelUrl) return null

  return (
    <article className="group flex w-[min(78vw,280px)] shrink-0 snap-start flex-col sm:w-[300px]">
      <div className="gold-gradient-ring rounded-2xl p-[2.5px] shadow-lg shadow-gold-500/20 transition-shadow duration-300 group-hover:shadow-gold-500/30">
        <div className="overflow-hidden rounded-[14px] bg-luxury-950 ring-1 ring-inset ring-white/10">
          <div className="relative aspect-[9/16] w-full">
            {playing ? (
              <>
                {!iframeReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <ReelCardBackdrop thumbnail={reel.thumbnail} />
                    <div className="relative z-10 flex flex-col items-center gap-3">
                      <div className="h-9 w-9 animate-spin rounded-full border-2 border-gold-500/30 border-t-gold-400" />
                      <p className="text-xs text-zinc-300">Loading video…</p>
                    </div>
                  </div>
                )}
                <iframe
                  src={embed}
                  title={
                    reel.title
                      ? `Instagram video: ${reel.title}`
                      : 'Instagram video'
                  }
                  className={cn(
                    'absolute inset-0 h-full w-full border-0 transition-opacity duration-300',
                    iframeReady ? 'opacity-100' : 'opacity-0'
                  )}
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                  onLoad={() => setIframeReady(true)}
                />
              </>
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                className="absolute inset-0 flex w-full flex-col items-center justify-center overflow-hidden text-left focus-ring"
                aria-label={
                  reel.title
                    ? `Play ${reel.title} on Instagram`
                    : 'Play Instagram video'
                }
              >
                <ReelCardBackdrop thumbnail={reel.thumbnail} />
                <div className="relative z-10 flex flex-col items-center gap-4 px-6 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-sm transition-transform duration-300 group-hover:scale-105">
                    <Play
                      className="ml-1 h-7 w-7 fill-white text-white"
                      aria-hidden
                    />
                  </div>
                  {kind && (
                    <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold-200/90 ring-1 ring-white/10">
                      {kindLabel[kind]}
                    </span>
                  )}
                  {reel.title && (
                    <p className="line-clamp-2 text-sm font-medium text-white/90">
                      {reel.title}
                    </p>
                  )}
                  <p className="text-xs text-zinc-400">Tap to play</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      <a
        href={reelUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors hover:text-gold-700"
      >
        <InstagramBrandIcon className="h-3.5 w-3.5" aria-hidden />
        Watch on Instagram
        <ExternalLink className="h-3 w-3 opacity-60" aria-hidden />
      </a>
    </article>
  )
}

interface FeaturedVideosScrollerProps {
  reels: InstagramReel[]
}

export function FeaturedVideosScroller({ reels }: FeaturedVideosScrollerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const updateEdges = () => {
    const el = scrollRef.current
    if (!el) return
    setEdges({
      left: el.scrollLeft > 12,
      right: el.scrollLeft < el.scrollWidth - el.clientWidth - 12,
    })
  }

  useEffect(() => {
    updateEdges()
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updateEdges)
    ro.observe(el)
    return () => ro.disconnect()
  }, [reels.length])

  const scrollBy = (direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -320 : 320,
      behavior: 'smooth',
    })
  }

  const showControls = reels.length > 1

  return (
    <div className="relative sm:-mx-6 lg:-mx-8">
      {showControls && edges.left && (
        <button
          type="button"
          onClick={() => scrollBy('left')}
          className="absolute left-2 top-[38%] z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold-500/30 bg-white/95 text-luxury-900 shadow-lg backdrop-blur-sm transition hover:bg-gold-50 focus-ring md:flex"
          aria-label="Scroll videos left"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {showControls && edges.right && (
        <button
          type="button"
          onClick={() => scrollBy('right')}
          className="absolute right-2 top-[38%] z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-gold-500/30 bg-white/95 text-luxury-900 shadow-lg backdrop-blur-sm transition hover:bg-gold-50 focus-ring md:flex"
          aria-label="Scroll videos right"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}

      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[#FAF8F4] to-transparent transition-opacity duration-300 sm:left-6 lg:left-8',
          edges.left ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#FAF8F4] to-transparent transition-opacity duration-300 sm:right-6 lg:right-8',
          edges.right ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />

      <div
        ref={scrollRef}
        onScroll={updateEdges}
        className={cn(
          'flex gap-4 overflow-x-auto pb-2 pt-1 sm:gap-5 sm:px-6 lg:px-8',
          'snap-x snap-mandatory scroll-smooth',
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {reels.map((reel) => (
          <FeaturedVideoCard key={reel.id} reel={reel} />
        ))}
      </div>
    </div>
  )
}
